import { useEffect, useRef } from "react";
import { PageType } from "@reconvert/reconvert-utils";
import { Platform } from "../models";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAppState } from "../app/slice/appSlice";
import { selectShopInfoState } from "../ReviewProviderWrapper";
import { updateShopInfoLocal, updateShopInfoThunk } from "../app/slice/shopinfoSlice";
import { sendSegmentEvent } from "../services/segment.service";
import { APP_PLATFORM } from "../config";
import { SegmentEventPageNames } from "../utils/constant";

const FIRST_PAGE_SELECTED_EVENT = "flow builder- first page selected";

// Fires once per store the first time the merchant is on a concrete page in Flow Builder —
// either by choosing one (sidebar / mobile locations) or by landing on one via deep link.
// Skips the Welcome screen (`showFlowBuilder === false`) so a URL `page` alone does not count.
export const useTrackFirstPageSelection = (): void => {
  const dispatch = useAppDispatch();
  const { shopInfo } = useAppSelector(selectShopInfoState);
  const { activePage, showFlowBuilder, isMobile } = useAppSelector(selectAppState);

  const storeClientId = shopInfo?.store_client_id;
  const storeName = shopInfo?.store_name;
  const isEligible = shopInfo?.first_page_selected !== "1";

  const inFlight = useRef(false);
  // Page we already tried to report. A failed attempt is not retried for the same page,
  // so a persistently failing endpoint can't be hammered — the merchant picking another
  // page is what triggers the next attempt.
  const attemptedPage = useRef<PageType | null>(null);
  const storeNameRef = useRef(storeName);

  storeNameRef.current = storeName;

  useEffect(() => {
    if (APP_PLATFORM !== Platform.Shopify || !isEligible || !storeClientId) return;

    if (!activePage || attemptedPage.current === activePage || inFlight.current) return;

    // Desktop welcome screen: activePage may already be set from the URL, but the merchant
    // has not chosen a page yet. Wait until they leave welcome (`showFlowBuilder === true`).
    // Mobile has no welcome gate via showFlowBuilder — any active page counts.
    if (!isMobile && !showFlowBuilder) return;

    inFlight.current = true;
    attemptedPage.current = activePage;

    sendSegmentEvent(storeClientId, FIRST_PAGE_SELECTED_EVENT, {
      "user id": storeClientId,
      "date & time": new Date().toISOString(),
      "store name": storeNameRef.current,
      platform: "Shopify",
      "store url": storeNameRef.current,
      "myshopify url": storeNameRef.current,
      "page clicked": SegmentEventPageNames[activePage],
    })
      .then(() => {
        dispatch(updateShopInfoLocal({ first_page_selected: "1" }));
        dispatch(updateShopInfoThunk({ first_page_selected: "1" }));
      })
      .catch(error => {
        console.error("Error sending first page selected event:", error);
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [activePage, showFlowBuilder, isMobile, storeClientId, isEligible, dispatch]);
};
